import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useContext, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { PasswordRestrictionMessageModal } from '@pages/Traders-Room/Trading-Accounts/modals/PasswordRestrictionMessage.modal'
import { ResetTradingAccountPasswordModal } from '@pages/Traders-Room/Trading-Accounts/modals/ResetTradingAccountPassword.modal'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useAccountInfo } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'
import { useEntityConfig } from '@context/TradingAccountEntityConfigurationContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { AlertNotice } from '@ui/components/AlertNotice'
import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'
import { PageSection } from '@ui/components/PageSection'
import { Tab } from '@ui/components/Tabs'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path-common'

import { NameDto } from '@model/NameDto'
import { DemoTradingAccount, TradingAccount, isLiveAccount } from '@model/TradingAccount'

import { useActiveTab } from '@hooks/useActiveTab'
import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { FirstTimeGuideCarousel } from '@shared/components/FirstTimeGuideCarousel/FirstTimeGuideCarousel'
import { InformationModal } from '@shared/modals/Information.modal'
import { SupportModal } from '@shared/modals/Support.modal'

import { useTradingAccountsMessage } from './hooks/useTradingAccountsMessage'

export type TradingAccountsOutletCtx = {
  handleSetSelectedTradingAccount: (tradingAccount: TradingAccount | DemoTradingAccount) => void
  handleChangePasswordRestricted: () => void
  handleResetPasswordRestricted: () => void
  handleChangeInvestorPasswordRestricted: () => void
  openResetPasswordModal: () => void
  openCampaignModal: () => void
  openWebTraderMT5IsUnavailableModal: () => void
  openWebTraderMT4IsUnavailableModal: () => void
  openWebTraderTickmillTraderIsUnavailableModal: () => void
  pageQuery: PageQuery | undefined
  setPageQuery: ((pageQuery?: PageQuery) => void) | undefined
  sortOptions: NameDto<string>[]
}

const useTradingAccountsTabs = () => {
  const { t } = useTranslation()

  const tabs: Tab[] = useMemo(
    () => [
      { label: t('Trading Account.Live'), path: 'live' },
      { label: t('Trading Account.Demo'), path: 'demo', tourTarget: 'trading.demoTab' },
      { label: t('Trading Account.Inactive'), path: 'inactive' },
    ],
    [t]
  )

  const { activePath } = useActiveTab(tabs, { includeNested: true, initialIndex: 0 })

  const isDemo = activePath === 'demo'
  const isLive = activePath === 'live'
  const isInactive = activePath === 'inactive'

  const title = useMemo(() => {
    switch (activePath) {
      case 'live':
        return t('Trading Account.Live Trading Accounts')
      case 'demo':
        return t('Trading Account.Demo Trading Accounts')
      case 'inactive':
        return t('Trading Account.Inactive Trading Accounts')
      default:
        return t('Trading Account.Trading Accounts')
    }
  }, [activePath, t])

  const sortOptions = useMemo(() => {
    return [{ id: 'Name', name: t('Trading Account.Account Number') }]
  }, [t])

  return {
    tabs,
    activePath,
    title,
    sortOptions,
    isDemo,
    isLive,
    isInactive,
  }
}

const useTradingAccountsTotals = (tabState: ReturnType<typeof useTradingAccountsTabs>) => {
  const { isDemo } = tabState

  const apiClient = useApiClient(ClientApiClient)

  const activeTAsTotals = useQuery({
    queryKey: queryKeys.tradingAccount.totals(),
    queryFn: () => apiClient.getTradingAccountsTotals(),
    refetchOnMount: 'always',
  })

  const demoTAsTotals = useQuery({
    queryKey: queryKeys.tradingAccount.demoTotals(),
    queryFn: () => apiClient.getDemoTradingAccountsTotals(),
    refetchOnMount: 'always',
    enabled: isDemo,
  })

  const displayedTotal = useMemo(() => {
    if (isDemo) {
      return demoTAsTotals.data?.totalBalanceInDisplayCurrency ?? 0
    }
    return activeTAsTotals.data?.totalBalanceInDisplayCurrency ?? 0
  }, [
    activeTAsTotals.data?.totalBalanceInDisplayCurrency,
    demoTAsTotals.data?.totalBalanceInDisplayCurrency,
    isDemo,
  ])

  return {
    activeTAsTotals,
    demoTAsTotals,
    displayedTotal,
  }
}

const useCreateButtonConfig = (tabState: ReturnType<typeof useTradingAccountsTabs>) => {
  const { isDemo, isInactive } = tabState

  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const { accountInfoLimits } = useAccountInfo()
  const { checkAccountOpenAccountAccess } = useAccountActionAccessCheck()
  const entityConfig = useEntityConfig()
  const tradingAccountMessage = useTradingAccountsMessage()

  const accountsConfiguration = useQuery({
    queryKey: queryKeys.tradingAccount.configuration(),
    queryFn: () => apiClient.getTradingAccountsConfiguration(),
  })

  const isDemoCreateAllowed = accountsConfiguration.data?.demoAccountCreateAllowed || false
  const isDemoCountryAllowed = entityConfig?.demoAccountAllowed || false

  const createData = useMemo(() => {
    if (isInactive) {
      return { disabled: true }
    }

    if (isDemo) {
      return {
        disabled: !isDemoCreateAllowed,
        onClick: () => navigate(CommonPaths.NewDemoTradingAccount),
        tooltip:
          !isDemoCreateAllowed && !isDemoCountryAllowed
            ? t(
                'Trading Account.It appears that the Demo Trading Account is not available in your country'
              )
            : !isDemoCreateAllowed
            ? t('Trading Account.You have reached the maximum number of Demo Trading Accounts')
            : undefined,
      }
    }

    return {
      disabled: accountInfoLimits?.hasReachedMaxTradingAccounts ?? false,
      tooltip: tradingAccountMessage,
      onClick: () =>
        checkAccountOpenAccountAccess(() => navigate(CommonPaths.NewLiveTradingAccount)),
    }
  }, [
    isInactive,
    isDemo,
    accountInfoLimits?.hasReachedMaxTradingAccounts,
    tradingAccountMessage,
    isDemoCreateAllowed,
    isDemoCountryAllowed,
    t,
    navigate,
    checkAccountOpenAccountAccess,
  ])

  return createData
}

export const TradingAccountsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const { formatMoney } = useFormatNumber()

  const { account } = useAccountReadContext()
  const [auth] = useContext(AuthSessionContext)
  const isMobile = useWindowResize()

  const tabState = useTradingAccountsTabs()
  const { displayedTotal } = useTradingAccountsTotals(tabState)
  const createButtonConfig = useCreateButtonConfig(tabState)

  const [pageQuery, setPageQuery] = useState<PageQuery | undefined>(undefined)

  const { tabs, title, sortOptions, isInactive } = tabState

  const selectedAccountRef = useRef<TradingAccount | DemoTradingAccount>(null)

  const passwordRestrictionModal = useModal(PasswordRestrictionMessageModal)

  const resetPasswordMessageModal = useModal(InformationModal, {
    title: t('Trading Account Password Reset Successfully'),
    description: `${t('Trading Account.A new trading account password has been sent to')} ${
      auth?.email
    }`,
  })

  const resetPasswordModal = useModal(ResetTradingAccountPasswordModal, {
    onConfirm: () => handleResetPassword(),
  })

  const campaignModal = useModal(SupportModal, {
    title: t('Trading Account.Campaign Account'),
    description: t('Trading Account.All relevant information about campaign'),
  })

  const webTraderMT5IsUnavailableModal = useModal(SupportModal, {
    title: t('Trading Account.WebTrader is unavailable'),
    description: () => (
      <>
        {t(
          'Trading Account.The WebTrader is currently not available. Check the other available MT5 applications'
        )}{' '}
        <span className='is-link' onClick={() => navigate('/dashboard/traders-room/platforms/mt5')}>
          {t('here')}.
        </span>
      </>
    ),
  })

  const webTraderMT4IsUnavailableModal = useModal(SupportModal, {
    title: t('Trading Account.WebTrader is unavailable'),
    description: () => (
      <>
        {t(
          'Trading Account.The WebTrader is currently not available. Check the other available MT4 applications'
        )}{' '}
        <span className='is-link' onClick={() => navigate('/dashboard/traders-room/platforms/mt4')}>
          {t('here')}.
        </span>
      </>
    ),
  })

  const webTraderTickmillTraderIsUnavailableModal = useModal(SupportModal, {
    title: t('Trading Account.Tickmill Trader Platform is unavailable'),
    description: () => (
      <>
        {t('Trading Account.Tickmill Trader Platform is currently not available')}{' '}
        <span className='is-link' onClick={() => navigate('/dashboard/traders-room/platforms')}>
          {t('here')}.
        </span>
      </>
    ),
  })

  const displayCurrencyInfoModal = useModal(InformationModal, {
    title: tabState.isDemo
      ? t('Trading Account.Total Demo Trading Accounts Balance')
      : t('Trading Account.Total Live Trading Accounts Balance'),
    description: () => (
      <Box direction='column' flex gap='x16'>
        <Modal.Body.Text>
          <Trans
            i18nKey='Wallet.Your current balance is the total of your {{ value }} balance(s)'
            values={
              tabState.isLive
                ? { value: 'live trading account' }
                : tabState.isDemo
                ? { value: 'demo trading account' }
                : undefined
            }
            components={{
              1: (
                <Link
                  title={t('Currency you selected')}
                  to='/profile/account-settings'
                  size='lg'
                  onClick={() => displayCurrencyInfoModal.close()}
                />
              ),
            }}
          />
        </Modal.Body.Text>
        {isMobile && createButtonConfig.tooltip && (
          <AlertNotice id={'accounts-count'} message={createButtonConfig.tooltip} type='info' />
        )}
      </Box>
    ),
  })

  const handleChangePasswordRestricted = () =>
    passwordRestrictionModal.open({
      title: t('Trading Account.Change Trading Account Password not Possible'),
      message: t('Trading Account.Changing Trading Account Password is not possible'),
    })
  const handleResetPasswordRestricted = () =>
    passwordRestrictionModal.open({
      title: t('Trading Account.Reset Password not Possible'),
      message: t('Trading Account.Reset Password is not possible at the moment'),
    })
  const handleChangeInvestorPasswordRestricted = () =>
    passwordRestrictionModal.open({
      title: t('Trading Account.Change Investor Trading Account Password'),
      message: t(
        'Trading Account.Change Investor Trading Account Password option is not possible at the moment'
      ),
    })

  const handleResetPassword = async () => {
    try {
      const accountToReset = selectedAccountRef.current
      if (accountToReset) {
        if (isLiveAccount(accountToReset?.accountGroupType)) {
          await apiClient.resetTradingAccountPassword({
            tradingAccountId: accountToReset.id,
            changeInvestorPassword: false,
          })
        } else {
          await apiClient.resetDemoTradingAccountPassword({
            tradingAccountId: accountToReset.id,
            changeInvestorPassword: false,
          })
        }
      }
    } finally {
      resetPasswordModal.close()
      resetPasswordMessageModal.open()
    }
  }

  const handleSetSelectedTradingAccount = (tradingAccount: TradingAccount | DemoTradingAccount) => {
    selectedAccountRef.current = tradingAccount
  }

  const outletCtx: TradingAccountsOutletCtx = {
    handleSetSelectedTradingAccount,
    handleChangePasswordRestricted,
    handleResetPasswordRestricted,
    handleChangeInvestorPasswordRestricted,
    openResetPasswordModal: () => resetPasswordModal.open(),
    openCampaignModal: () => campaignModal.open(),
    openWebTraderMT5IsUnavailableModal: () => webTraderMT5IsUnavailableModal.open(),
    openWebTraderMT4IsUnavailableModal: () => webTraderMT4IsUnavailableModal.open(),
    openWebTraderTickmillTraderIsUnavailableModal: () =>
      webTraderTickmillTraderIsUnavailableModal.open(),
    pageQuery,
    setPageQuery,
    sortOptions,
  }

  return (
    <>
      <FirstTimeGuideCarousel disabled />
      <PageSection
        title={
          <>
            {title}{' '}
            {!isInactive && (
              <SensitiveData>
                ≈{formatMoney(displayedTotal, account?.displayCurrency?.id)}
              </SensitiveData>
            )}
          </>
        }
        infoData={!isInactive ? () => displayCurrencyInfoModal.open() : undefined}
        tabData={{ tabs }}
        sortOptions={sortOptions}
        pageQuery={pageQuery}
        setPageQuery={setPageQuery}
        renderActions={[
          {
            disabled: createButtonConfig.disabled,
            onClick: createButtonConfig.onClick,
            icon: CirclePlusIcon,
            tooltip:
              !isMobile && createButtonConfig.tooltip
                ? { content: createButtonConfig.tooltip }
                : undefined,
            title: t('Create'),
            tourTarget: 'trading.create',
          },
        ]}
      />
      <Outlet context={outletCtx} />
    </>
  )
}
